import { useState } from "react";
import { Eye, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";

/**
 * Fixed top bar shown while an admin is acting as a tenant or owner. Rendered
 * globally; renders nothing when not impersonating.
 */
export const ImpersonationBanner = () => {
  const { isImpersonating, impersonatedUser, stopImpersonating } = useAuth();
  const { t } = useLanguage();
  const [exiting, setExiting] = useState(false);

  if (!isImpersonating || !impersonatedUser) return null;

  const label = impersonatedUser.name || impersonatedUser.email;
  const roleLabel =
    impersonatedUser.kind === "owner"
      ? t("admin.impersonation.roleOwner", "owner")
      : t("admin.impersonation.roleTenant", "tenant");

  const handleExit = async () => {
    setExiting(true);
    try {
      await stopImpersonating();
    } finally {
      setExiting(false);
    }
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] bg-amber-500 text-amber-950 shadow-[0_-2px_8px_rgba(0,0,0,0.15)]">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2 text-sm">
        <div className="flex min-w-0 items-center gap-2 font-medium">
          <Eye className="h-4 w-4 shrink-0" />
          <span className="truncate">
            {t("admin.impersonation.actingAs", "Acting as")} {roleLabel}:{" "}
            <strong>{label}</strong>
            {impersonatedUser.name && (
              <span className="font-normal text-amber-900/80">
                {" "}
                ({impersonatedUser.email})
              </span>
            )}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleExit}
          disabled={exiting}
          className="shrink-0 gap-1.5 border-amber-700 bg-white/80 text-amber-950 hover:bg-white"
        >
          <LogOut className="h-3.5 w-3.5" />
          {exiting
            ? t("admin.impersonation.exiting", "Exiting…")
            : t("admin.impersonation.exit", "Exit")}
        </Button>
      </div>
    </div>
  );
};
